import { i18n } from '@/i18n';
import { UpdateBusinessViewModel } from '../UpdateBusinessViewModel';
import { computed, ref } from 'vue';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { formOptionsAggregateBusiness, unitOptionsMapping, validateRuleKeyMap } from '../constants';
import { useForms } from '@/corePagesModule/composables/useForm';
import { useUpdateFieldState } from './useUpdateFieldState';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { convertLeadTimeByUnit, convertLeadTimeToDays } from '@/coreDomainsModule/leadtime';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { UserAccount } from '@/coreDomainsModule/entities/users';

/**
 * UpdateAggregateBusinessFormを使用するためのComposable
 * @param option - オプション
 */
export const useUpdateAggregateBusinessForm = (option?: { validateRuleKeyMap?: Record<string, ValidateRule[]> }) => {
  const t = i18n.t;
  const updateBusinessVM = new UpdateBusinessViewModel();

  const isEdited = ref<boolean>(false);
  const currentOpenSelectInputName = ref('');

  const fieldItems = computed((): FieldItem[] => {
    const items = formOptionsAggregateBusiness.map((name: string) => {
      return {
        name,
        rules: option?.validateRuleKeyMap?.[name] ?? validateRuleKeyMap[name],
      };
    });
    return items;
  });

  const { fieldState, allValidate, resetForm } = useForms(fieldItems.value);
  const { addNewOption, updateValue, updateUserValue } = useUpdateFieldState(fieldState, isEdited);

  /**
   * 国内輸送日数の入力値を数値に変換する。空文字・数値変換不可の場合はfallbackを返す
   * @param raw - フィールドの入力値
   * @param fallback - 変換できない場合に返す値
   */
  const parseInTransitDays = <T>(raw: string, fallback: T): number | T => {
    if (raw === '') return fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  /**
   * フォームを初期化する
   * @param item - アイテム
   * @param currentItem - 現在のアイテム
   */
  const initializeForm = (item: Partial<IBusiness>, currentItem?: Partial<IBusiness>) => {
    const targetLeadTimeUnit = currentItem?.leadTimeUnit ?? item.leadTimeUnit;
    const targetLeadTime = convertLeadTimeByUnit(currentItem?.leadTime ?? item.leadTime ?? 0, targetLeadTimeUnit ?? '');
    const targetStdInventoryIndices = currentItem?.stdInventoryIndices ?? item.stdInventoryIndices;
    resetForm(fieldItems.value);
    for (let i = 0; i < fieldItems.value.length; i++) {
      const keyName = fieldItems.value[i].name;
      switch (keyName) {
        case 'warehouseName':
          fieldState[keyName].setValue(currentItem?.warehouse ?? item.warehouse ?? '', false);
          break;
        case 'orderPackage':
          fieldState[keyName].setValue(((currentItem?.orderPackage ?? item.orderPackage) || '').toString(), false);
          break;
        case 'leadTime':
          fieldState[keyName].setValue(targetLeadTime > 0 ? targetLeadTime.toString() : '');
          break;
        case 'inTransitDays':
          fieldState[keyName].setValue((currentItem?.inTransitDays ?? item.inTransitDays ?? 0).toString(), false);
          break;
        case 'leadTimeUnit':
          fieldState[keyName].setValue(targetLeadTimeUnit ? t(unitOptionsMapping[targetLeadTimeUnit]) : '', false);
          break;
        case 'stdInventoryIndicesMin':
          fieldState[keyName].setValue(
            targetStdInventoryIndices?.標準在庫指数?.min ? targetStdInventoryIndices['標準在庫指数'].min.toString() : '',
            false
          );
          break;
        case 'stdInventoryIndicesMax':
          fieldState[keyName].setValue(
            targetStdInventoryIndices?.標準在庫指数?.max ? targetStdInventoryIndices['標準在庫指数'].max.toString() : '',
            false
          );
          break;
        case 'stdInventoryIndicesUnit':
          fieldState[keyName].setValue(
            targetStdInventoryIndices?.標準在庫指数?.option.unit
              ? t(unitOptionsMapping[targetStdInventoryIndices.標準在庫指数.option.unit])
              : '',
            false
          );
          break;
        case 'aggregateBusinessResponsibleUser':
          fieldState[keyName].setValue(
            currentItem?.aggregateBusinessResponsibleUsers?.map((user: UserAccount) => user.email).join(',') ??
              item.aggregateBusinessResponsibleUsers?.map((user: UserAccount) => user.email).join(',') ??
              '',
            false
          );
          break;
        default:
          fieldState[keyName].setValue(currentItem?.[keyName] ?? item[keyName] ?? '', false);
      }
    }
  };

  /**
   * 変更した取引情報と元々の取引情報をマージしたIBusinessを返す
   * @param baseItem - 元の取引
   */
  const createUpdatedAggregateBusiness = (baseItem?: Partial<IBusiness>): IBusiness => {
    const aggregateBusinessResponsibleUsersString = fieldState['aggregateBusinessResponsibleUser'].value.value;
    const convertedLeadTimeUnit = updateBusinessVM.convertDateUnit(fieldState['leadTimeUnit'].value.value);
    return BusinessFactory.create({
      ...baseItem,
      warehouseNumber: fieldState['warehouseNumber'].value.value,
      warehouse: fieldState['warehouseName'].value.value,
      supplierNumber: fieldState['supplierNumber'].value.value,
      supplierName: fieldState['supplierName'].value.value,
      businessNumber: fieldState['businessNumber'].value.value,
      businessName: fieldState['businessName'].value.value,
      package: fieldState['package'].value.value,
      unit: fieldState['unit'].value.value,
      manufacturer: updateBusinessVM.convertUnassingedToUndefined(fieldState['manufacturer'].value.value),
      leadTime: convertLeadTimeToDays(Number(fieldState['leadTime'].value.value) || 0, convertedLeadTimeUnit),
      leadTimeUnit: convertedLeadTimeUnit,
      inTransitDays: parseInTransitDays(fieldState['inTransitDays'].value.value, 0),
      MOQ: Number(fieldState['MOQ'].value.value),
      orderPackage: Number(fieldState['orderPackage'].value.value),
      purchaseUnitPrice: Number(fieldState['purchaseUnitPrice'].value.value) || undefined,
      stdInventoryIndices: {
        標準在庫指数: {
          max: Number(fieldState['stdInventoryIndicesMax'].value.value) || 0,
          min: Number(fieldState['stdInventoryIndicesMin'].value.value) || 0,
          option: {
            unit: updateBusinessVM.convertDateUnit(fieldState['stdInventoryIndicesUnit'].value.value),
          },
        },
      },
      userUniqueNumber: fieldState.userUniqueNumber.value.value,
      aggregateBusinessResponsibleUsers:
        aggregateBusinessResponsibleUsersString.length > 0
          ? aggregateBusinessResponsibleUsersString.split(',').map((userId) => UserAccountFactory.create({ email: userId }))
          : [],
    });
  };

  /**
   * 変更した取引情報を返す
   */
  const createAggregateBusinessPatch = (): Partial<IBusiness> => ({
    warehouseNumber: fieldState['warehouseNumber'].value.value || undefined,
    warehouse: fieldState['warehouseName'].value.value || undefined,
    supplierNumber: fieldState['supplierNumber'].value.value || undefined,
    supplierName: fieldState['supplierName'].value.value || undefined,
    businessNumber: fieldState['businessNumber'].value.value || undefined,
    businessName: fieldState['businessName'].value.value || undefined,
    package: fieldState['package'].value.value || undefined,
    unit: fieldState['unit'].value.value || undefined,
    manufacturer: fieldState['manufacturer'].value.value || undefined,
    leadTime: ((v) => (v > 0 ? v : undefined))(
      convertLeadTimeToDays(
        Number(fieldState['leadTime'].value.value) || 0,
        updateBusinessVM.convertDateUnit(fieldState['leadTimeUnit'].value.value)
      )
    ),
    leadTimeUnit: updateBusinessVM.convertDateUnit(fieldState['leadTimeUnit'].value.value) || undefined,
    inTransitDays: parseInTransitDays(fieldState['inTransitDays'].value.value, undefined),
    MOQ: Number(fieldState['MOQ'].value.value) || undefined,
    orderPackage: Number(fieldState['orderPackage'].value.value) || undefined,
    purchaseUnitPrice: Number(fieldState['purchaseUnitPrice'].value.value) || undefined,
    stdInventoryIndices: {
      標準在庫指数: {
        max: Number(fieldState['stdInventoryIndicesMax'].value.value) || 0,
        min: Number(fieldState['stdInventoryIndicesMin'].value.value) || 0,
        option: {
          unit: updateBusinessVM.convertDateUnit(fieldState['stdInventoryIndicesUnit'].value.value),
        },
      },
    },
    userUniqueNumber: fieldState.userUniqueNumber.value.value || undefined,
    aggregateBusinessResponsibleUsers:
      fieldState['aggregateBusinessResponsibleUser'].value.value.length > 0
        ? fieldState['aggregateBusinessResponsibleUser'].value.value
            .split(',')
            .map((userId) => UserAccountFactory.create({ email: userId }))
        : undefined,
  });

  return {
    isEdited,
    currentOpenSelectInputName,
    fieldState,
    allValidate,
    createUpdatedAggregateBusiness,
    updateValue,
    updateUserValue,
    addNewOption,
    initializeForm,
    createAggregateBusinessPatch,
  };
};
